/**
 * RoleMappingEditor (Sprint C12 Lane 4)
 *
 * Editor for an IdP `claimMapping.roleMapping` rule list. Each rule
 * describes how an IdP claim value maps to an Aaim role assignment. A rule
 * either assigns a fixed role (`role`) OR derives the role from another
 * claim by name (`roleFrom`) -- exactly one of the two is required.
 *
 * The component is fully controlled. Rule add/edit happens in a sub-Dialog
 * so the parent CreateIdpModal stays clean. The Dialog enforces the
 * `role XOR roleFrom` invariant at submit time.
 *
 * Composes only from `@aaimplatform/ui`.
 *
 * @see schema-registry/identity-provider-registry.openapi.yaml#RoleMappingRule
 */

import { useState } from 'react';
import { Button, Input, Select, Dialog, BodyText, Badge, TableScroll } from '@aaimplatform/ui';
import type { components } from '@aaimplatform/institutional-types/identity-provider-registry';

type RoleMappingRule = components['schemas']['RoleMappingRule'];

export interface RoleMappingEditorProps {
  /** Current rule list. Order is preserved as-is. */
  value: RoleMappingRule[];
  /** Fires whenever a rule is added, edited, or removed. */
  onChange: (next: RoleMappingRule[]) => void;
  /**
   * Available Aaim roles for the static `role` field. If not provided, the
   * editor falls back to a free-form text input so an operator can type any
   * role name supported by the platform.
   */
  availableRoles?: string[];
  /** When true, all interaction is disabled. */
  disabled?: boolean;
  /** Optional id used to associate descriptive text with the editor. */
  id?: string;
}

interface DraftRule {
  /** Origin index in `value`, or -1 for a brand new rule. */
  originIndex: number;
  claim: string;
  matchValue: string;
  roleStrategy: 'static' | 'fromClaim';
  role: string;
  roleFrom: string;
  error: string | null;
}

const EMPTY_DRAFT: DraftRule = {
  originIndex: -1,
  claim: '',
  matchValue: '',
  roleStrategy: 'static',
  role: '',
  roleFrom: '',
  error: null,
};

/**
 * Validate a draft rule and translate it into the canonical RoleMappingRule
 * shape if valid. Returns `{ ok: false, error }` on validation failure.
 */
function commitDraft(
  draft: DraftRule,
): { ok: true; rule: RoleMappingRule } | { ok: false; error: string } {
  const claim = draft.claim.trim();
  const matchValue = draft.matchValue.trim();
  if (!claim) return { ok: false, error: 'Claim is required' };
  if (!matchValue) return { ok: false, error: 'Match value is required' };

  if (draft.roleStrategy === 'static') {
    const role = draft.role.trim();
    if (!role) return { ok: false, error: 'Role is required when using a static role' };
    return {
      ok: true,
      rule: { claim, value: matchValue, role },
    };
  }

  const roleFrom = draft.roleFrom.trim();
  if (!roleFrom) {
    return {
      ok: false,
      error: 'RoleFrom claim name is required when using a dynamic role',
    };
  }
  return {
    ok: true,
    rule: { claim, value: matchValue, roleFrom },
  };
}

/**
 * Build a fresh editor draft from an existing rule. Decides which strategy
 * to display based on whether `role` or `roleFrom` is populated.
 */
function ruleToDraft(rule: RoleMappingRule, originIndex: number): DraftRule {
  return {
    originIndex,
    claim: rule.claim,
    matchValue: rule.value,
    roleStrategy: rule.roleFrom ? 'fromClaim' : 'static',
    role: rule.role ?? '',
    roleFrom: rule.roleFrom ?? '',
    error: null,
  };
}

export function RoleMappingEditor({
  value,
  onChange,
  availableRoles,
  disabled = false,
  id,
}: RoleMappingEditorProps) {
  const [editorOpen, setEditorOpen] = useState(false);
  const [draft, setDraft] = useState<DraftRule>(EMPTY_DRAFT);

  const openCreate = () => {
    if (disabled) return;
    setDraft(EMPTY_DRAFT);
    setEditorOpen(true);
  };

  const openEdit = (index: number) => {
    if (disabled) return;
    const rule = value[index];
    if (!rule) return;
    setDraft(ruleToDraft(rule, index));
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setDraft(EMPTY_DRAFT);
  };

  const handleSubmitDraft = () => {
    const result = commitDraft(draft);
    if (!result.ok) {
      setDraft({ ...draft, error: result.error });
      return;
    }
    const next = [...value];
    if (draft.originIndex >= 0) {
      next[draft.originIndex] = result.rule;
    } else {
      next.push(result.rule);
    }
    onChange(next);
    closeEditor();
  };

  const handleDelete = (index: number) => {
    if (disabled) return;
    const next = value.filter((_, i) => i !== index);
    onChange(next);
  };

  return (
    <div className="space-y-3" id={id}>
      <BodyText size="sm" color="secondary">
        Define rules that map IdP claim values to Aaim roles. Each rule must
        specify either a static role or a claim-derived role -- not both.
      </BodyText>

      {value.length === 0 ? (
        <div
          className="border border-dashed border-recessed rounded-sm p-4 text-center"
          data-testid="role-mapping-empty"
        >
          <BodyText size="sm" color="tertiary">
            No role mapping rules configured. Click "Add Rule" to create one.
          </BodyText>
        </div>
      ) : (
        <div className="border border-recessed rounded-sm overflow-hidden">
          <TableScroll label="Role mappings">
          <table className="w-full min-w-table-dense text-sm">
            <thead className="bg-warm-50 border-b border-recessed">
              <tr>
                <th scope="col" className="text-left px-3 py-2 font-sans font-medium text-primary">
                  Claim
                </th>
                <th scope="col" className="text-left px-3 py-2 font-sans font-medium text-primary">
                  Value
                </th>
                <th scope="col" className="text-left px-3 py-2 font-sans font-medium text-primary">
                  Role
                </th>
                <th scope="col" className="text-left px-3 py-2 font-sans font-medium text-primary">
                  RoleFrom
                </th>
                <th scope="col" className="w-32 px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {value.map((rule, index) => (
                <tr
                  key={`${rule.claim}-${rule.value}-${index}`}
                  className="border-b border-recessed last:border-b-0"
                >
                  <td className="px-3 py-2 font-mono text-xs text-primary">{rule.claim}</td>
                  <td className="px-3 py-2 font-mono text-xs text-primary">{rule.value}</td>
                  <td className="px-3 py-2 text-primary">
                    {rule.role ? <Badge variant="success">{rule.role}</Badge> : <span className="text-tertiary">--</span>}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-primary">
                    {rule.roleFrom ?? <span className="text-tertiary font-sans">--</span>}
                  </td>
                  <td className="px-3 py-2 text-right space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(index)}
                      disabled={disabled}
                      aria-label={`Edit rule for ${rule.claim}`}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(index)}
                      disabled={disabled}
                      aria-label={`Delete rule for ${rule.claim}`}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </TableScroll>
        </div>
      )}

      <div className="flex justify-end">
        <Button variant="secondary" size="sm" onClick={openCreate} disabled={disabled}>
          Add Rule
        </Button>
      </div>

      <Dialog
        open={editorOpen}
        onOpenChange={(next) => (next ? setEditorOpen(true) : closeEditor())}
        title={draft.originIndex >= 0 ? 'Edit Role Mapping Rule' : 'Add Role Mapping Rule'}
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={closeEditor}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSubmitDraft}>
              Save Rule
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {draft.error && (
            <div className="p-3 bg-error/10 border border-error/30 rounded-sm">
              <span className="text-sm text-error">{draft.error}</span>
            </div>
          )}

          <div>
            <label className="block text-sm font-sans font-medium text-primary mb-1">
              IdP Claim <span className="text-error">*</span>
            </label>
            <Input
              value={draft.claim}
              onChange={(e) => setDraft({ ...draft, claim: e.target.value, error: null })}
              placeholder="custom:staff_role"
              aria-label="Claim name"
            />
            <span className="text-xs text-tertiary mt-1 block">
              Name of the claim emitted by the IdP that this rule will inspect.
            </span>
          </div>

          <div>
            <label className="block text-sm font-sans font-medium text-primary mb-1">
              Match Value <span className="text-error">*</span>
            </label>
            <Input
              value={draft.matchValue}
              onChange={(e) => setDraft({ ...draft, matchValue: e.target.value, error: null })}
              placeholder="loan_officer"
              aria-label="Match value"
            />
            <span className="text-xs text-tertiary mt-1 block">
              The exact claim value that triggers this rule. For multi-valued
              claims (arrays), the rule fires when any element matches.
            </span>
          </div>

          <div>
            <span className="block text-sm font-sans font-medium text-primary mb-2">
              Assignment Strategy
            </span>
            <div className="space-y-2">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="roleStrategy"
                  value="static"
                  checked={draft.roleStrategy === 'static'}
                  onChange={() => setDraft({ ...draft, roleStrategy: 'static', error: null })}
                  className="mt-1"
                />
                <div>
                  <span className="text-sm font-sans text-primary">Static role</span>
                  <span className="text-xs text-tertiary block">
                    Assign a fixed role when the claim matches.
                  </span>
                </div>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="roleStrategy"
                  value="fromClaim"
                  checked={draft.roleStrategy === 'fromClaim'}
                  onChange={() =>
                    setDraft({ ...draft, roleStrategy: 'fromClaim', error: null })
                  }
                  className="mt-1"
                />
                <div>
                  <span className="text-sm font-sans text-primary">Role from claim</span>
                  <span className="text-xs text-tertiary block">
                    Read the assigned role from a different IdP claim's value.
                  </span>
                </div>
              </label>
            </div>
          </div>

          {draft.roleStrategy === 'static' ? (
            <div>
              <label className="block text-sm font-sans font-medium text-primary mb-1">
                Role <span className="text-error">*</span>
              </label>
              {availableRoles && availableRoles.length > 0 ? (
                <Select
                  value={draft.role}
                  onChange={(e) => setDraft({ ...draft, role: e.target.value, error: null })}
                  aria-label="Role"
                >
                  <option value="">Select a role...</option>
                  {availableRoles.map((roleName) => (
                    <option key={roleName} value={roleName}>
                      {roleName}
                    </option>
                  ))}
                </Select>
              ) : (
                <Input
                  value={draft.role}
                  onChange={(e) => setDraft({ ...draft, role: e.target.value, error: null })}
                  placeholder="fi_loan_officer"
                  aria-label="Role"
                />
              )}
            </div>
          ) : (
            <div>
              <label className="block text-sm font-sans font-medium text-primary mb-1">
                Role From Claim <span className="text-error">*</span>
              </label>
              <Input
                value={draft.roleFrom}
                onChange={(e) => setDraft({ ...draft, roleFrom: e.target.value, error: null })}
                placeholder="custom:groups"
                aria-label="Role from claim"
              />
              <span className="text-xs text-tertiary mt-1 block">
                The IdP claim name whose value(s) will be used as Aaim role names.
              </span>
            </div>
          )}
        </div>
      </Dialog>
    </div>
  );
}
